import React, { useState, useEffect } from 'react';
import { FiCheckCircle } from 'react-icons/fi';

const SingleChoiceQuestion = ({ 
  question, 
  onAnswer, 
  currentAnswer = null,
  isSubmitted = false,
  timeSpent = 0 
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Initialize selected answer from current answer or empty state
  useEffect(() => {
    if (currentAnswer !== null && currentAnswer !== undefined) {
      setSelectedAnswer(currentAnswer);
    } else {
      setSelectedAnswer(null);
    }
  }, [currentAnswer]);

  const handleAnswerSelect = (answer) => {
    if (isSubmitted) return; // Prevent changes after submission
    setSelectedAnswer(answer);
  };

  const handleSubmit = async () => {
    if (isSubmitting || selectedAnswer === null) return;

    setIsSubmitting(true);
    
    try {
      await onAnswer({
        questionId: question.id,
        selectedAnswer,
        type: 'SINGLE_CHOICE',
        timeSpent
      });
      
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting single choice answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAnswerStatus = (answer) => {
    if (!showFeedback || !currentAnswer) return null;
    
    if (answer === currentAnswer) {
      return 'correct';
    } else if (answer === selectedAnswer) {
      return 'incorrect';
    }
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'correct': return 'border-green-500 bg-green-50';
      case 'incorrect': return 'border-red-500 bg-red-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'correct': return <FiCheckCircle className="text-green-600 text-lg" />;
      case 'incorrect': return <FiCheckCircle className="text-red-600 text-lg" />;
      default: return null;
    }
  };

  // Handle case where there are no options yet
  if (!question.options || question.options.length === 0) {
    return (
      <div className="single-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
        <div className="mb-6">
          {/* Question Text */}
          <div className="text-black text-base font-bold leading-relaxed mb-4">
            {question.text}
          </div>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-gray-800">
              <FiCheckCircle className="text-lg" />
              <span className="font-medium">Question Preview</span>
            </div>
            <p className="text-gray-700 mt-2">
              This is a single choice question. The question text is displayed above, but the answer options are not yet configured. 
              When fully configured, you'll see multiple options with only one correct answer.
            </p>
            <div className="mt-3 p-3 bg-white rounded border">
              <p className="text-sm text-gray-600">
                <strong>Question:</strong> {question.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="single-choice-question bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6">
      {/* Question Text */}
      <div className="mb-6">
        <div className="text-black text-base font-bold leading-relaxed mb-6">
          {question.text}
        </div>
      </div>

      {/* Answer Options */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-black mb-4">Select the correct answer:</h4>
        
        <div className="space-y-3">
          {question.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const status = getAnswerStatus(index);
            
            return (
              <label
                key={option.id || index}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  isSelected 
                    ? 'border-blue-600 bg-blue-50' 
                    : status 
                    ? getStatusColor(status)
                    : 'border-gray-300 bg-white hover:border-blue-600 hover:bg-blue-50'
                }`}
              >
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  checked={isSelected}
                  onChange={() => handleAnswerSelect(index)}
                  className="hidden"
                  disabled={isSubmitted}
                />
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isSelected 
                    ? 'border-blue-600 bg-blue-600' 
                    : 'border-gray-400 bg-transparent'
                }`}>
                  {isSelected && (
                    <div className="w-2.5 h-2.5 bg-white rounded-full" />
                  )}
                </div>
                <span className="text-black font-medium">{option.text}</span>
                
                {/* Status Icon */}
                {status && (
                  <div className="ml-auto">
                    {getStatusIcon(status)}
                  </div>
                )}
              </label>
            );
          })}
        </div>
      </div>



      {/* Feedback */}
      {showFeedback && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-black mb-2">Answer Submitted</h5>
          <p className="text-sm text-black">
            Your answer has been recorded. You can review it in the question navigator.
          </p>
        </div>
      )}
    </div>
  );
};

export default SingleChoiceQuestion; 